import type { ReactNode } from 'react';
import { Icon } from '@capsico/ui';
import type { CountryCode, CustomerDish } from '@capsico/contracts';
import type { GroupRule, SlotRule } from '../hooks/useCustomise';
import { money } from '../utils/money';
import { Sheet, VegMark } from './ui';

/**
 * C3 customisation sheet (V/W sheet grammar): dish header, size radio list, add-on groups with
 * min / max rule captions, combo choices, and a pinned bar with − n + and "Add item · ₹total".
 * Presentation only — state lives in useCustomise.
 */
export function CustomiseSheet({
  dish,
  country,
  labels,
  variantId,
  onVariant,
  groups,
  onPick,
  slots,
  onSlot,
  quantity,
  onIncrease,
  onDecrease,
  total,
  complete,
  onAdd,
  onClose,
}: {
  dish: CustomerDish;
  country: CountryCode | null;
  labels: {
    size: string;
    required: string;
    optional: string;
    rule: (rule: { min: number; max: number }) => string;
    pick: (count: number) => string;
    addToCart: (price: string) => string;
    incomplete: string;
    close: string;
  };
  variantId: string | null;
  onVariant: (id: string) => void;
  groups: GroupRule[];
  onPick: (rule: GroupRule, optionId: string) => void;
  slots: SlotRule[];
  onSlot: (slot: SlotRule, itemId: string) => void;
  quantity: number;
  onIncrease: () => void;
  onDecrease: () => void;
  total: number;
  complete: boolean;
  onAdd: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet
      onClose={onClose}
      footer={
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-28 items-center justify-between rounded-lg bg-brand px-1 text-on-brand">
            <button
              type="button"
              aria-label="−"
              onClick={onDecrease}
              className="flex size-9 items-center justify-center disabled:opacity-50"
              disabled={quantity <= 1}
            >
              <Icon name="minus" size={16} />
            </button>
            <span className="text-sm font-bold tabular">{quantity}</span>
            <button
              type="button"
              aria-label="+"
              onClick={onIncrease}
              className="flex size-9 items-center justify-center"
            >
              <Icon name="plus" size={16} />
            </button>
          </div>
          <button
            type="button"
            onClick={onAdd}
            disabled={!complete}
            className="h-11 flex-1 rounded-lg bg-brand text-sm font-bold text-on-brand active:scale-[0.98] disabled:bg-tile disabled:text-ink-3"
          >
            {complete ? labels.addToCart(money(country, total)) : labels.incomplete}
          </button>
        </div>
      }
    >
      <div className="flex items-start gap-3">
        {dish.photo && (
          <img src={dish.photo.smallUrl} alt="" className="size-14 rounded-lg object-cover" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <VegMark type={dish.foodType} />
            <h2 className="truncate text-base font-bold text-ink">{dish.name}</h2>
          </div>
          {dish.description && (
            <p className="mt-0.5 line-clamp-2 text-xs text-ink-2">{dish.description}</p>
          )}
        </div>
      </div>

      {dish.variants.length > 1 && (
        <OptionGroup title={labels.size} caption={labels.required} required>
          {dish.variants.map((variant) => (
            <OptionRow
              key={variant.id}
              kind="radio"
              on={variant.id === variantId}
              label={variant.name}
              price={money(country, variant.priceMinor)}
              onClick={() => onVariant(variant.id)}
            />
          ))}
        </OptionGroup>
      )}

      {slots.map((slot) => (
        <OptionGroup key={slot.id} title={slot.name} caption={labels.pick(slot.pick)} required>
          {slot.options.map((option) => (
            <OptionRow
              key={option.itemId}
              kind={slot.pick === 1 ? 'radio' : 'check'}
              on={slot.chosen.includes(option.itemId)}
              label={option.name}
              foodType={option.foodType}
              onClick={() => onSlot(slot, option.itemId)}
            />
          ))}
        </OptionGroup>
      ))}

      {groups.map((rule) => (
        <OptionGroup
          key={rule.group.id}
          title={rule.group.name}
          caption={`${rule.min > 0 ? labels.required : labels.optional} · ${labels.rule(rule)}`}
          required={rule.min > 0}
        >
          {rule.group.options.map((option) => (
            <OptionRow
              key={option.id}
              kind={rule.max === 1 ? 'radio' : 'check'}
              on={rule.chosen.includes(option.id)}
              label={option.name}
              foodType={option.foodType}
              price={option.priceMinor > 0 ? `+ ${money(country, option.priceMinor)}` : undefined}
              onClick={() => onPick(rule, option.id)}
            />
          ))}
        </OptionGroup>
      ))}
    </Sheet>
  );
}

function OptionGroup({
  title,
  caption,
  required,
  children,
}: {
  title: string;
  caption: string;
  required: boolean;
  children: ReactNode;
}) {
  return (
    <section className="mt-5">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <span
          className={`text-caption ${required ? 'font-semibold text-brand-ink' : 'text-ink-3'}`}
        >
          {caption}
        </span>
      </div>
      <div className="divide-y divide-hair overflow-hidden rounded-xl bg-surface shadow-[inset_0_0_0_1px_var(--color-hair)]">
        {children}
      </div>
    </section>
  );
}

function OptionRow({
  kind,
  on,
  label,
  price,
  foodType,
  onClick,
}: {
  kind: 'radio' | 'check';
  on: boolean;
  label: string;
  price?: string;
  foodType?: 'veg' | 'non_veg' | 'egg';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role={kind === 'radio' ? 'radio' : 'checkbox'}
      aria-checked={on}
      onClick={onClick}
      className="flex w-full items-center gap-3 px-3 py-3 text-left active:bg-tile"
    >
      {foodType && <VegMark type={foodType} size={12} />}
      <span className="min-w-0 flex-1 truncate text-sm text-ink">{label}</span>
      {price && <span className="text-xs text-ink-2 tabular">{price}</span>}
      {kind === 'radio' ? (
        <span
          className={`flex size-5 items-center justify-center rounded-full ${
            on
              ? 'bg-brand shadow-[inset_0_0_0_1.5px_var(--color-ink)]'
              : 'shadow-[inset_0_0_0_1.5px_var(--color-control)]'
          }`}
        >
          {on && <span className="size-2 rounded-full bg-ink" />}
        </span>
      ) : (
        <span
          className={`flex size-5 items-center justify-center rounded ${
            on ? 'bg-brand text-on-brand' : 'shadow-[inset_0_0_0_1.5px_var(--color-ink-3)]'
          }`}
        >
          {on && <Icon name="check" size={14} />}
        </span>
      )}
    </button>
  );
}
